import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Bot, Download, Filter, Info, RefreshCcw, Search } from 'lucide-react';
import { Badge, Button, ErrorState, Input, PageHeader, Spinner } from '@helios/ui';
import { ChartDrillDown, type DrillCohort } from '@helios/ui/charts';
import { SingleScreenDashboard } from '@/components/dashboard/SingleScreenDashboard';
import { useAuth } from '@/features/auth/authContext';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import { useCommandCenter, useCommandCenterActions } from '@/hooks/useCommandCenter';
import { useRealtimeDashboard } from '@/hooks/useRealtimeDashboard';
import type {
  CommandCenterFilters,
  CommandCenterPayload,
  RiskLevel,
} from '@/services/commandCenterClient';
import { ExecutiveDashboard, type ExecutiveView } from './ExecutiveDashboard';
import { ExecutivePriorityPanel } from './ExecutivePriorityPanel';
import type { ExecutiveOverviewNavigation } from './ExecutiveOverview';

const views = [
  'overview',
  'finance',
  'delivery',
  'growth',
  'risk',
  'intelligence',
  'system',
] as const;
const aliases: Record<string, ExecutiveView> = {
  'financial-outlook': 'finance',
  'enterprise-risk': 'risk',
  operations: 'delivery',
  'delivery-ai': 'intelligence',
  'enterprise-ai': 'intelligence',
};
const selectStyle =
  'h-8 min-w-0 rounded border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function CommandCenterPage() {
  const auth = useAuth();
  // Local dialogs and authoring drafts cannot survive an actor/grant/sign-in change.
  const key = JSON.stringify([
    auth.tenant?.id,
    auth.user?.id,
    auth.sessionId,
    auth.authVersion,
    auth.accessMode,
    getSessionOriginKey(),
    [...auth.permissions].sort(),
  ]);
  return <ExecutiveWorkspace key={key} />;
}
export function ExecutiveSummaryPage() {
  return <CommandCenterPage />;
}

function ExecutiveWorkspace() {
  const { permissions } = useAuth();
  const header = useRef<HTMLElement>(null);
  const refreshButton = useRef<HTMLButtonElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Navigation survives numeric-source reads; actor/session/grant changes still
  // remount this workspace and reset it without retaining any source records.
  const [overviewNavigation, setOverviewNavigation] = useState<ExecutiveOverviewNavigation>({
    section: 'company',
    group: 'summary',
  });
  const [inspection, setInspection] = useState<{
    cohort: DrillCohort;
    snapshot: CommandCenterPayload;
  } | null>(null);
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const query = useCommandCenter(filters);
  const actions = useCommandCenterActions(filters);
  const realtime = useRealtimeDashboard();
  const validData = isCommandCenterPayload(query.data) ? query.data : undefined;
  const payload = query.isFetching || query.isError ? undefined : validData;
  // Native evidence belongs to the exact snapshot that produced its source
  // action. A later successful read cannot revive facts withdrawn by a refresh.
  const cohort = payload && inspection?.snapshot === payload ? inspection.cohort : null;
  useEffect(() => {
    if (inspection && inspection.snapshot !== payload) setInspection(null);
  }, [inspection, payload]);
  const inspect = (next: DrillCohort) => {
    if (payload) setInspection({ cohort: next, snapshot: payload });
  };
  const busy =
    query.isFetching || actions.generateBrief.isPending || actions.exportReport.isPending;
  const error = actions.generateBrief.error ?? actions.exportReport.error;
  const brief = actions.generateBrief.data;
  const report = actions.exportReport.data;
  const rawView = searchParams.get('view') ?? 'overview';
  const view: ExecutiveView = views.includes(rawView as ExecutiveView)
    ? (rawView as ExecutiveView)
    : (aliases[rawView] ?? 'overview');
  const canExport =
    payload?.permissions.canExportReport &&
    hasPermission(permissions, PERMISSIONS.REPORTS_LIBRARY_EXECUTE) &&
    hasPermission(permissions, PERMISSIONS.REPORTS_LIBRARY_READ);
  const update = (key: string, value: string) => {
    setInspection(null);
    actions.generateBrief.reset?.();
    actions.exportReport.reset?.();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value.trim()) next.set(key, value.trim());
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };
  const run = async (kind: 'brief' | 'export') => {
    try {
      if (kind === 'brief') await actions.generateBrief.mutateAsync();
      else await actions.exportReport.mutateAsync();
    } catch {
      /* The scoped mutation exposes the current-origin error only. */
    }
  };
  const sectors = Array.from(
    new Set(validData?.projectMap.markers.flatMap((row) => (row.sector ? [row.sector] : [])) ?? []),
  ).sort();
  const regions = Array.from(
    new Set(validData?.projectMap.markers.flatMap((row) => (row.state ? [row.state] : [])) ?? []),
  ).sort();
  const filtered = Boolean(filters.search || filters.sector || filters.region || filters.risk);
  return (
    <div
      className="executive-workspace flex min-h-0 flex-1 flex-col gap-2 lg:h-full lg:overflow-hidden"
      aria-label="Executive command center workspace"
    >
      <PageHeader
        ref={header}
        tabIndex={-1}
        aria-label="Executive command center header"
        className="executive-workspace__header focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2"
        eyebrow="Executive management"
        title="Executive Command Center"
        description="Company position, delivery exceptions and decisions."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral">
              {payload
                ? `Assembled ${new Date(payload.generatedAt).toLocaleTimeString()}`
                : query.isFetching
                  ? 'Refreshing sources'
                  : 'Source unavailable'}
            </Badge>
            <Button
              ref={refreshButton}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setInspection(null);
                void query.refetch();
              }}
              disabled={busy}
            >
              <RefreshCcw className="h-4 w-4" aria-hidden="true" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="ai"
              size="sm"
              disabled={busy || !payload?.permissions.canGenerateAdvisorBrief}
              onClick={() => void run('brief')}
            >
              <Bot className="h-4 w-4" aria-hidden="true" />
              Record advisor brief
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy || !canExport}
              title={
                !canExport
                  ? `Requires ${PERMISSIONS.REPORTS_LIBRARY_EXECUTE} and report export access`
                  : undefined
              }
              onClick={() => void run('export')}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Board brief
            </Button>
          </div>
        }
      />
      <section
        className="executive-workspace__filters border-border bg-card shrink-0 rounded-lg border p-2"
        aria-label="Portfolio filters"
      >
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_150px_150px_130px_auto]">
          <label className="relative">
            <span className="sr-only">Search projects</span>
            <Search
              className="text-muted-foreground pointer-events-none absolute left-2 top-2 h-4 w-4"
              aria-hidden="true"
            />
            <Input
              className="h-8 pl-8"
              value={filters.search}
              onChange={(event) => update('search', event.target.value)}
              placeholder="Search project, client or city"
            />
          </label>
          <label>
            <span className="sr-only">Sector</span>
            <select
              className={`${selectStyle} w-full`}
              value={filters.sector}
              onChange={(event) => update('sector', event.target.value)}
            >
              <option value="">All sectors</option>
              {Array.from(new Set([...sectors, ...(filters.sector ? [filters.sector] : [])])).map(
                (item) => (
                  <option key={item}>{item}</option>
                ),
              )}
            </select>
          </label>
          <label>
            <span className="sr-only">Region</span>
            <select
              className={`${selectStyle} w-full`}
              value={filters.region}
              onChange={(event) => update('region', event.target.value)}
            >
              <option value="">All regions</option>
              {Array.from(new Set([...regions, ...(filters.region ? [filters.region] : [])])).map(
                (item) => (
                  <option key={item}>{item}</option>
                ),
              )}
            </select>
          </label>
          <label>
            <span className="sr-only">Risk</span>
            <select
              className={`${selectStyle} w-full`}
              value={filters.risk}
              onChange={(event) => update('risk', event.target.value)}
            >
              <option value="">All risk</option>
              {(['CRITICAL', 'HIGH', 'MODERATE', 'LOW'] as const).map((item) => (
                <option key={item} value={item}>
                  {item.charAt(0) + item.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!filtered}
              onClick={() => {
                setInspection(null);
                setSearchParams(
                  (current) => {
                    const next = new URLSearchParams(current);
                    ['search', 'sector', 'region', 'risk'].forEach((key) => next.delete(key));
                    return next;
                  },
                  { replace: true },
                );
              }}
            >
              <Filter className="h-4 w-4" aria-hidden="true" />
              Clear
            </Button>
            <details className="executive-workspace__filter-scope relative">
              <summary
                className="text-muted-foreground focus-visible:ring-ring flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2"
                aria-label="Portfolio filter scope"
              >
                <Info className="h-4 w-4" aria-hidden="true" />
              </summary>
              <p className="border-border bg-popover text-muted-foreground absolute right-0 top-full z-30 mt-1 w-72 rounded-md border p-3 text-xs shadow-lg">
                Filters apply to portfolio projects, project alerts and advisor previews. Company
                finance, growth and field sources retain their own company scope and calendars.
              </p>
            </details>
          </div>
        </div>
      </section>
      {brief || report || error ? (
        <div className="border-border bg-card rounded border p-2 text-xs" role="status">
          {error?.message ??
            (report ? (
              <>
                <p>
                  {report.title} · {report.pageCount ?? 'Page count unavailable'} pages · SHA256{' '}
                  {report.sha256 ?? 'unavailable'}.
                </p>
                <p className="text-2xs text-muted-foreground mt-1">{report.sourceScope}</p>
                <a
                  className="text-context-primary mt-2 inline-block underline"
                  href={report.downloadUrl}
                  download={report.fileName}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download board brief PDF
                </a>
                <Link className="text-context-primary ml-3 underline" to="/reports?view=library">
                  Open report ledger
                </Link>
              </>
            ) : (
              `${brief?.title}: ${brief?.summary}`
            ))}
        </div>
      ) : null}
      <ExecutivePriorityPanel
        compact={view === 'overview'}
        onRestoreFocus={() => {
          if (refreshButton.current?.isConnected && !refreshButton.current.disabled)
            refreshButton.current.focus({ preventScroll: true });
          else header.current?.focus({ preventScroll: true });
        }}
        renderWorkspace={(priorities) =>
          query.isLoading || query.isFetching ? (
            <div
              className="border-border bg-card text-muted-foreground flex min-h-[300px] flex-1 items-center justify-center gap-2 rounded-lg border text-sm"
              role="status"
            >
              <Spinner className="h-4 w-4" />
              Reading current executive sources. Figures are withheld during refresh.
            </div>
          ) : !payload ? (
            <ErrorState
              title="Executive Command Center unavailable"
              description="The current tenant-scoped source snapshot could not be read. Previously loaded figures are withheld."
              action={
                <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
                  Retry sources
                </Button>
              }
            />
          ) : (
            <SingleScreenDashboard
              className="executive-dashboard-tabs"
              panelClassName={
                view === 'overview' ? 'executive-workspace__overview-panel' : undefined
              }
              activeTabId={view}
              onTabChange={(id) => update('view', id)}
              tabs={views.map((id) => ({
                id,
                label: id.charAt(0).toUpperCase() + id.slice(1),
                content: (
                  <ExecutiveDashboard
                    payload={payload}
                    view={id}
                    inspect={inspect}
                    realtime={realtime}
                    priorities={priorities}
                    overviewNavigation={overviewNavigation}
                    onOverviewNavigationChange={setOverviewNavigation}
                  />
                ),
              }))}
            />
          )
        }
      />
      <ChartDrillDown
        cohort={cohort}
        onOpenChange={(open) => {
          if (!open) setInspection(null);
        }}
        renderLink={(href, children) => <Link to={href}>{children}</Link>}
      />
    </div>
  );
}

function filtersFromParams(params: URLSearchParams): CommandCenterFilters {
  const risk = params.get('risk');
  return {
    search: params.get('search') ?? '',
    sector: params.get('sector') ?? '',
    region: params.get('region') ?? '',
    risk: risk && ['LOW', 'MODERATE', 'HIGH', 'CRITICAL'].includes(risk) ? (risk as RiskLevel) : '',
  };
}
function isCommandCenterPayload(value: unknown): value is CommandCenterPayload {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CommandCenterPayload>;
  return (
    typeof candidate.generatedAt === 'string' &&
    Boolean(candidate.tenant?.id) &&
    Array.isArray(candidate.kpis) &&
    Array.isArray(candidate.projectMap?.markers) &&
    Array.isArray(candidate.projectPerformance?.byRisk) &&
    Array.isArray(candidate.projectPerformance?.healthDistribution) &&
    Array.isArray(candidate.alerts) &&
    Array.isArray(candidate.advisor?.recommendations) &&
    Boolean(candidate.permissions) &&
    Boolean(candidate.summary)
  );
}
